import apiClient from "../../../utils/apiClient";

/** Whether a save leaves its question answered — what a student's answer row records. */
const answeredBy = (patch) => (
  (Array.isArray(patch.selectedOptionIds) && patch.selectedOptionIds.length > 0)
  || (typeof patch.textAnswer === "string" && patch.textAnswer.trim() !== "")
  || (typeof patch.code === "string" && patch.code.trim() !== "")
);
const touchesAnswer = (patch) => ["selectedOptionIds", "textAnswer", "code"].some((k) => k in patch);

/**
 * previewAdapter — ExamRunner for a staff member previewing an assessment the
 * way a student sits it, from `GET /assessments/:id/preview`.
 *
 * Nothing is stored. Answers stay in this tab, a timed section moves on by its
 * own clock here, and the submit only reports how far the preview got. The one
 * thing that reaches the server is what cannot be faked: a passage's PDF (a
 * short-lived link, as a student gets) and "Run code" on a coding problem's
 * sample tests.
 *
 * The anti-cheat rules are deliberately NOT enforced — a preview that forced
 * full screen and blocked copy and paste would trap the person checking it —
 * and the preview page says which ones students get instead.
 *
 *   payload   the preview response: { attempt, questions, passages }
 *   base      the assessment's API path, `/assessments/<id>`
 */
export default function createPreviewAdapter({ payload, base }) {
  const answered = new Set();
  const progress = payload.attempt?.sectionProgress || [];
  const total = (payload.questions || []).length;

  return {
    async loadAttempt() {
      const a = payload.attempt || {};
      const assessment = a.assessment || {};
      return {
        attempt: {
          _id: "preview",
          answers: a.answers || [],
          startedAt: new Date().toISOString(),
          timeLeftSeconds: a.timeLeftSeconds ?? null,
          sectionProgress: progress,
          currentSectionIndex: 0,
          sectionClock: a.sectionClock || null,
        },
        questions: payload.questions || [],
        passages: payload.passages || [],
        exam: {
          title: assessment.title || "Assessment",
          instructions: assessment.instructions || "",
          isSectioned: !!assessment.isSectioned,
          negativeMarking: !!assessment.negativeMarking,
          negativeMarkValue: assessment.negativeMarkValue || 0,
        },
        policy: {
          fullscreenRequired: false,
          disableCopyPaste: false,
          disableRightClick: false,
          blockPrint: false,
          watermark: false,
          heartbeatSeconds: 0,
          signals: {},
        },
      };
    },

    async saveAnswer(questionId, patch = {}) {
      if (touchesAnswer(patch)) {
        if (answeredBy(patch)) answered.add(String(questionId));
        else answered.delete(String(questionId));
      }
      return {};
    },

    async reportEvent() {
      return { recorded: false };
    },

    heartbeat: null,

    /** The passage PDF, through the authors' own link to it. */
    async passageFileUrl(passageId) {
      const { data } = await apiClient.get(`/quiz-passages/${passageId}/file`);
      return data?.url || null;
    },

    /** The next timed section, by the section times on the paper. */
    async nextSection(from) {
      const next = Number(from) + 1;
      if (next >= progress.length) return { last: true };
      return {
        sectionClock: {
          sequential: true,
          index: next,
          secondsLeft: (Number(progress[next]?.timeLimit) || 0) * 60,
          last: next === progress.length - 1,
          finished: false,
          count: progress.length,
        },
      };
    },

    async runCode(questionId, body) {
      const { data } = await apiClient.post(`${base}/preview/code/run`, { questionId, ...body });
      return data;
    },

    async submit({ timeSpent }) {
      return { submitted: true, preview: true, answered: answered.size, total, timeSpent };
    },
  };
}
